// src/config/modules.js
// Module registry for the ROOTS platform.
// Top-level sections are the primary nav (modules).
// Each section has children — the actual pages within that module.
// To add a new page: add to a section's children array.
// To add a new module: add a new section entry.

import { lazy } from 'react';

export const SECTION_REGISTRY = [
  {
    id: 'production',
    name: 'Production Records',
    icon: 'ClipboardList',
    active: true,
    hasSetup: true, // has per-user configuration (categories, overrides, review)
    children: [
      {
        id: 'dashboard',
        slug: 'dashboard',
        name: 'Dashboard',
        icon: 'LayoutDashboard',
        path: '/dashboard',
        component: lazy(() => import('../pages/dashboard/DashboardPage')),
        roles: ['admin', 'manager', 'user'],
      },
      {
        id: 'ebr',
        slug: 'ebr',
        name: 'Batch Records',
        icon: 'FileStack',
        path: '/ebr',
        component: lazy(() => import('../pages/ebr/EBRPage')),
        roles: ['admin', 'manager', 'user'],
      },
    ],
  },
  {
    id: 'guestManager',
    name: 'Guest Manager',
    icon: 'UserCheck',
    active: true,
    hasSetup: true,
    children: [
      {
        id: 'guest-manager',
        slug: 'guest-manager',
        name: 'Guest Manager',
        icon: 'UserCheck',
        path: '/guest-manager',
        component: lazy(() => import('../pages/guest-manager/GuestManagerPage')),
        roles: ['admin', 'manager'],
        hasSetup: true,
      },
    ],
  },
  {
    id: 'sops',
    name: 'SOPs & Documents',
    icon: 'FileText',
    active: false,
    comingSoon: true,
    children: [],
  },
  {
    id: 'qr',
    name: 'QR Codes',
    icon: 'QrCode',
    active: false,
    comingSoon: true,
    children: [],
  },
  {
    id: 'settings',
    name: 'Settings',
    icon: 'SlidersHorizontal',
    active: true,
    children: [
      {
        id: 'admin',
        slug: 'admin',
        name: 'Admin',
        icon: 'Users',
        path: '/admin',
        component: lazy(() => import('../pages/admin/AdminPage')),
        roles: ['admin'],
      },
    ],
  },
];

// Flat list of all routable page modules (used by App.js for route generation)
export function getAllModules() {
  return SECTION_REGISTRY.flatMap(s => s.children || []).filter(m => m.component);
}

// Sections visible to a given role and moduleAccess list
// moduleAccess = null means no restrictions (admin or unset)
export function getSectionsForRole(role, moduleAccess = null) {
  return SECTION_REGISTRY.map(section => ({
    ...section,
    children: (section.children || []).filter(m => m.roles.includes(role)),
  })).filter(section => {
    if (!section.comingSoon && section.children.length === 0) return false;
    if (role === 'admin') return true;
    if (moduleAccess && !moduleAccess.includes(section.id)) return false;
    return true;
  });
}

// Find which section a page slug belongs to
export function getSectionForSlug(slug) {
  return SECTION_REGISTRY.find(s => (s.children || []).some(m => m.slug === slug));
}

// Sections that can have per-user access control (excludes settings which is role-gated)
export function getControllableSections() {
  return SECTION_REGISTRY.filter(s => s.id !== 'settings');
}

// Get a page module by slug
export function getModule(slug) {
  return getAllModules().find(m => m.slug === slug);
}
